import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import {
  Tree,
  Input,
  Card,
  Space,
  Button,
  Typography,
  Empty,
  Dropdown,
  Menu,
  Tooltip,
} from "antd";
import {
  EllipsisOutlined,
  ReloadOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { DataNode } from "antd/lib/tree";

const { Search } = Input;
const { Text } = Typography;

// 修改全局样式，添加调整间距的CSS
// 修改全局样式，调整箭头方向
const globalStyles = `
  .tree-panel .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-treenode {
    width: 100% !important;
    height: 32px !important;
    line-height: 32px !important;
  }
  
  .tree-panel .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-node-content-wrapper {
    width: 100% !important;
    display: flex !important;
    padding: 0 4px !important;
    height: 32px !important;
    line-height: 32px !important;
  }
  
  .tree-panel .ant-tree .ant-tree-list .ant-tree-list-holder .ant-tree-title {
    display: flex !important;
    width: 100% !important;
    justify-content: space-between !important;
    align-items: center !important;
  }
  
  /* 确保复选框可以点击 */
  .tree-panel .ant-tree .ant-tree-checkbox {
    pointer-events: auto !important;
    z-index: 10 !important;
  }
  
  .tree-panel .ant-tree .ant-tree-checkbox-wrapper {
    pointer-events: auto !important;
    z-index: 10 !important;
  }
  
  .tree-panel .ant-tree .ant-tree-node-content-wrapper {
    pointer-events: auto !important;
  }
  
  /* 确保标题内容不会阻止复选框点击 */
  .tree-panel .ant-tree .ant-tree-title {
    pointer-events: none !important;
  }
  
  .tree-panel .ant-tree .ant-tree-title > * {
    pointer-events: none !important;
  }
  
  /* 确保整个标题区域不会阻止复选框点击，但允许悬停显示 Tooltip */
  .tree-panel .ant-tree .ant-tree-node-content-wrapper:hover .ant-tree-title {
    pointer-events: none !important;
  }
  
  .tree-panel .ant-tree-switcher .anticon {
    color: #bfbfbf !important;
  }
  
  .tree-panel .ant-tree-switcher {
    padding: 0 2px !important;
    width: 18px !important;
  }
  
  .tree-panel .tree-node-add-button {
    color: #bfbfbf !important;
  }
  
  .tree-panel .tree-node-add-button:hover {
    color: #1890ff !important;
  }
  
  .compact-tree .ant-tree-treenode {
    padding: 0 !important;
  }
  
  .tree-panel .ant-tree {
    width: 100% !important;
  }
  
  .tree-panel .ant-tree .ant-tree-list {
    width: 100% !important;
  }
  
  .tree-panel .ant-tree .ant-tree-list .ant-tree-list-holder {
    width: 100% !important;
  }
`;

// 树节点数据接口
export interface TreeNodeData {
  key: string;
  title: string | React.ReactNode; // 支持字符串或 React 节点
  children?: TreeNodeData[];
  isLeaf?: boolean;
  disabled?: boolean; // 是否禁用该节点
  [key: string]: any;
}

// 权限相关接口
export interface PermissionTreeNodeData extends TreeNodeData {
  permission?: any;
  serviceName?: string;
  isAssigned?: boolean;
  type?: "MODULE" | "FUNCTION";
}

// 权限树选择配置
export interface PermissionSelectionConfig {
  showCheckbox?: boolean;
  disabledKeys?: string[];
  rolePermissionSources?: Record<string, string[]>;
  selectedPermissionIds?: string[];
}

// 树组件 Ref 接口
export interface GenericPanelTreeRef {
  getSelectedPermissions?: () => any[]; // 获取选中的权限项（用于权限树）
  getSelectedKeys?: () => React.Key[]; // 获取选中的键
}

// 自定义节点标题渲染函数类型
export type NodeTitleRender = (
  node: TreeNodeData,
  level: number
) => React.ReactNode;

// 组件属性接口 - 添加 selectedKeys 属性
export interface GenericPanelTreeProps {
  title?: string;
  treeData: TreeNodeData[];
  loading?: boolean;
  buttons?: React.ReactNode[];
  onSelect?: (selectedKeys: React.Key[], info: any) => void;
  onMenuClick?: (node: TreeNodeData, action: string) => void;
  onRefresh?: () => void;
  height?: number | string;
  showSearch?: boolean;
  searchPlaceholder?: string; // 搜索框占位符
  showLine?: boolean | { showLeafIcon: boolean };
  showIcon?: boolean;
  defaultExpandAll?: boolean;
  defaultExpandedKeys?: string[];
  defaultSelectedKeys?: string[];
  menuItems?: { key: string; label: string }[];
  switcherIcon?: React.ReactNode;
  // 添加新属性
  selectedKeys?: React.Key[]; // 支持从外部控制选中状态
  expandedKeys?: React.Key[]; // 支持从外部控制展开状态
  onExpand?: (expandedKeys: React.Key[], info: any) => void; // 展开事件回调
  showActions?: boolean; // 是否显示操作列
  onEdit?: (node: TreeNodeData) => void; // 编辑回调
  onDelete?: (node: TreeNodeData) => void; // 删除回调
  // 标题样式相关属性
  headerPadding?: string | number; // 标题的 padding
  // Card 组件属性
  cardProps?: React.ComponentProps<typeof Card>;
  // 自定义菜单按钮渲染
  customMenuButton?: (node: TreeNodeData) => React.ReactNode;
  // 复选框相关属性
  checkable?: boolean; // 是否显示复选框
  checkedKeys?: React.Key[]; // 选中的复选框键
  onCheck?: (
    checkedKeys:
      | React.Key[]
      | { checked: React.Key[]; halfChecked: React.Key[] },
    info: any
  ) => void; // 复选框变化回调
  defaultCheckedKeys?: React.Key[]; // 默认选中的复选框键
  checkStrictly?: boolean; // 父子节点是否关联选中
  // 权限相关属性
  disabledKeys?: string[]; // 禁用的节点key列表
  rolePermissionSources?: Record<string, string[]>; // 权限来源映射（权限ID -> 角色名称列表）
  resetKey?: string; // 用于强制重置组件状态的key
  // 自定义节点标题渲染
  nodeTitleRender?: NodeTitleRender; // 自定义节点标题渲染函数
  // 数据源引用（用于 getSelectedPermissions）
  dataSourceRef?: React.MutableRefObject<any[]>; // 原始数据源引用
  // 布局相关
  customLayout?: boolean; // 是否使用自定义布局（搜索框固定，树可滚动）
  className?: string; // 自定义类名
  style?: React.CSSProperties; // 自定义样式
  extra?: React.ReactNode; // 额外的操作区域
}

/**
 * 通用树面板组件
 */
const GenericPanelTree = forwardRef<GenericPanelTreeRef, GenericPanelTreeProps>(
  (
    {
      title = "数据树",
      treeData = [],
      loading = false,
      buttons = [],
      onSelect,
      onMenuClick,
      onRefresh,
      height = 400,
      showSearch = true,
      searchPlaceholder = "搜索...",
      showLine = true,
      showIcon = false,
      defaultExpandAll = true,
      defaultExpandedKeys = [],
      defaultSelectedKeys = [],
      menuItems = [],
      switcherIcon,
      // 添加到参数列表
      selectedKeys,
      expandedKeys,
      onExpand,
      showActions = true,
      onEdit,
      onDelete,
      headerPadding,
      cardProps = {},
      customMenuButton,
      // 复选框相关参数
      checkable = false,
      checkedKeys: externalCheckedKeys,
      onCheck,
      defaultCheckedKeys = [],
      checkStrictly = false,
      // 权限相关参数
      disabledKeys = [],
      rolePermissionSources = {},
      resetKey,
      // 自定义节点标题渲染
      nodeTitleRender,
      // 数据源引用
      dataSourceRef,
      // 布局相关
      customLayout = false,
      className,
      style,
      extra,
    },
    ref
  ) => {
    // 状态定义
    const [searchValue, setSearchValue] = useState<string>("");
    const [internalExpandedKeys, setInternalExpandedKeys] =
      useState<React.Key[]>(defaultExpandedKeys);
    const [internalCheckedKeys, setInternalCheckedKeys] =
      useState<React.Key[]>(defaultCheckedKeys);
    const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
    const [filteredTreeData, setFilteredTreeData] =
      useState<TreeNodeData[]>(treeData);
    const [treeContainerHeight, setTreeContainerHeight] = useState<number>(0);
    const [isUserInteracting, setIsUserInteracting] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const treeDataRef = useRef<TreeNodeData[]>(treeData);
    const lastResetKeyRef = useRef<string>("");
    const hasInitializedRef = useRef<boolean>(false);
    const lastAssignedIdsRef = useRef<string>("");

    // 更新 treeDataRef
    useEffect(() => {
      treeDataRef.current = treeData;
    }, [treeData]);

    // 使用外部 checkedKeys 或内部状态
    const checkedKeys =
      externalCheckedKeys !== undefined
        ? externalCheckedKeys
        : internalCheckedKeys;

    // 暴露给父组件的方法
    useImperativeHandle(
      ref,
      () => ({
        getSelectedPermissions: () => {
          if (!dataSourceRef?.current) {
            return [];
          }
          // 只保留叶子节点的权限ID（排除模块节点）
          const permissionIds = checkedKeys.filter((key) => {
            const isPermissionNode = (
              permissions: any[],
              targetKey: string
            ): boolean => {
              for (const permission of permissions) {
                if (
                  permission.id === targetKey &&
                  permission.type === "FUNCTION"
                ) {
                  return true;
                }
                if (
                  permission.children &&
                  isPermissionNode(permission.children, targetKey)
                ) {
                  return true;
                }
              }
              return false;
            };
            return isPermissionNode(dataSourceRef.current, String(key));
          });

          // 将选中的ID转换为权限项数组
          const selectedPermissions = permissionIds
            .map((id) => {
              const findPermission = (
                permissions: any[],
                targetId: string
              ): any | null => {
                for (const permission of permissions) {
                  if (permission.id === targetId) {
                    return permission;
                  }
                  if (permission.children) {
                    const found = findPermission(permission.children, targetId);
                    if (found) return found;
                  }
                }
                return null;
              };
              return findPermission(dataSourceRef.current, String(id));
            })
            .filter(Boolean);

          return selectedPermissions;
        },
        getSelectedKeys: () => {
          return checkedKeys;
        },
      }),
      [checkedKeys, dataSourceRef]
    );

    // 当 resetKey 变化时，强制重置组件状态
    useEffect(() => {
      if (resetKey && resetKey !== lastResetKeyRef.current) {
        lastResetKeyRef.current = resetKey;
        setIsUserInteracting(false);
        lastAssignedIdsRef.current = "__FORCE_UPDATE__";
        hasInitializedRef.current = false;
        setInternalCheckedKeys(defaultCheckedKeys);
        setInternalExpandedKeys(defaultExpandedKeys);
      }
    }, [resetKey, defaultCheckedKeys, defaultExpandedKeys]);

    // 使用外部expandedKeys或内部状态
    const currentExpandedKeys = expandedKeys || internalExpandedKeys;

    // 处理 disabledKeys：在树数据中标记禁用的节点
    const processedTreeData = useMemo(() => {
      if (disabledKeys.length === 0 && !nodeTitleRender) {
        return treeData;
      }

      const processNode = (
        node: TreeNodeData,
        level: number = 1
      ): TreeNodeData => {
        const isDisabled = disabledKeys.includes(node.key);
        const sources = rolePermissionSources[node.key] || [];

        let processedNode: TreeNodeData = {
          ...node,
          disabled: isDisabled || node.disabled,
        };

        // 如果有自定义节点标题渲染函数，使用它
        if (nodeTitleRender) {
          processedNode = {
            ...processedNode,
            title: nodeTitleRender(node, level),
          };
        }

        // 处理子节点
        if (node.children) {
          processedNode.children = node.children.map((child) =>
            processNode(child, level + 1)
          );
        }

        return processedNode;
      };

      return treeData.map((node) => processNode(node, 1));
    }, [treeData, disabledKeys, rolePermissionSources, nodeTitleRender]);

    // 从节点标题中提取文本（支持 React 节点）
    const getNodeTitleText = useCallback((node: TreeNodeData): string => {
      if (typeof node.title === "string") {
        return node.title;
      }

      // 从React元素中提取文本
      const extractTextFromElement = (element: any): string => {
        if (typeof element === "string") return element;
        if (typeof element === "number") return element.toString();
        if (!element) return "";

        if (element.props && element.props.children) {
          if (Array.isArray(element.props.children)) {
            return element.props.children.map(extractTextFromElement).join(" ");
          }
          return extractTextFromElement(element.props.children);
        }

        return "";
      };

      return extractTextFromElement(node.title);
    }, []);

    // 过滤树数据
    const filterTree = React.useCallback(
      (value: string) => {
        const expandKeys: React.Key[] = [];

        // 递归查找匹配节点及其父节点
        const filterData = (data: TreeNodeData[]): TreeNodeData[] => {
          return data
            .map((node) => {
              const titleText = getNodeTitleText(node);
              const matchNode = titleText
                .toLowerCase()
                .includes(value.toLowerCase());

              // 处理子节点
              const children = node.children ? filterData(node.children) : [];
              const matchChildren = children.length > 0;

              // 如果自身匹配或有匹配的子节点，则保留节点
              if (matchNode || matchChildren) {
                expandKeys.push(node.key);
                return {
                  ...node,
                  children: children.length > 0 ? children : undefined,
                };
              }
              return null;
            })
            .filter((node) => node !== null) as TreeNodeData[];
        };

        const filtered = filterData(processedTreeData);
        setFilteredTreeData(filtered);
        if (!expandedKeys) {
          setInternalExpandedKeys(expandKeys);
        }
        setAutoExpandParent(true);
      },
      [processedTreeData, expandedKeys, getNodeTitleText]
    );

    // 处理节点选择（当没有复选框时）
    const handleSelect = useCallback(
      (selectedKeys: React.Key[], info: any) => {
        if (onSelect) {
          onSelect(selectedKeys, info);
        }
      },
      [onSelect]
    );

    // 当原始树数据变化时，更新过滤后的树数据
    useEffect(() => {
      if (searchValue) {
        filterTree(searchValue);
      } else {
        setFilteredTreeData(processedTreeData);
      }
    }, [processedTreeData, searchValue, filterTree]);

    // 当外部expandedKeys变化时，同步到内部状态
    useEffect(() => {
      if (expandedKeys) {
        setInternalExpandedKeys(expandedKeys);
      }
    }, [expandedKeys]);

    // 添加这个新的 useEffect 以处理 defaultExpandAll
    useEffect(() => {
      if (
        defaultExpandAll &&
        processedTreeData.length > 0 &&
        !expandedKeys &&
        !searchValue &&
        !hasInitializedRef.current
      ) {
        // 收集所有节点的 key
        const getAllKeys = (nodes: TreeNodeData[]): React.Key[] => {
          let keys: React.Key[] = [];
          nodes.forEach((node) => {
            keys.push(node.key);
            if (node.children && node.children.length > 0) {
              keys = [...keys, ...getAllKeys(node.children)];
            }
          });
          return keys;
        };

        const allKeys = getAllKeys(processedTreeData);
        setInternalExpandedKeys(allKeys);
        setAutoExpandParent(true);
        hasInitializedRef.current = true;
      }
    }, [processedTreeData, defaultExpandAll, expandedKeys, searchValue]);

    // 同步选中的键 - 当树数据变化时更新（仅在非用户交互时）
    useEffect(() => {
      if (
        checkable &&
        processedTreeData.length > 0 &&
        !isUserInteracting &&
        externalCheckedKeys === undefined
      ) {
        // 从树数据中提取已分配的权限ID（如果有 isAssigned 标记）
        const extractAssignedIds = (nodes: TreeNodeData[]): React.Key[] => {
          const ids: React.Key[] = [];
          nodes.forEach((node) => {
            if (
              (node as PermissionTreeNodeData).isAssigned &&
              (node as PermissionTreeNodeData).type !== "MODULE"
            ) {
              ids.push(node.key);
            }
            if (node.children) {
              ids.push(...extractAssignedIds(node.children));
            }
          });
          return ids;
        };

        const assignedIds = extractAssignedIds(processedTreeData);
        if (assignedIds.length > 0) {
          const assignedIdsSignature = [...assignedIds].sort().join(",");
          if (assignedIdsSignature !== lastAssignedIdsRef.current) {
            lastAssignedIdsRef.current = assignedIdsSignature;
            setInternalCheckedKeys(assignedIds);
          }
        }
      }
    }, [processedTreeData, checkable, isUserInteracting, externalCheckedKeys]);

    // 计算树容器高度
    useEffect(() => {
      if (containerRef.current) {
        const updateHeight = () => {
          if (containerRef.current) {
            // 获取 Card 的实际高度
            const cardHeight = containerRef.current.offsetHeight;
            // Card header 高度（约 56px）+ body padding (12px * 2 = 24px)
            // search 高度（如果显示，约 40px + marginTop 16px）
            // tree-container 的 marginTop (16px)
            const headerHeight = 56;
            const searchHeight = showSearch ? 56 : 0; // search input height + margin
            const height = cardHeight - headerHeight - 24 - searchHeight - 16;
            setTreeContainerHeight(height > 0 ? height : 0);
          }
        };
        updateHeight();
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
      }
    }, [showSearch]);

    // 处理搜索
    const handleSearch = (value: string) => {
      setSearchValue(value);
      if (value) {
        filterTree(value);
      } else {
        setFilteredTreeData(processedTreeData);
        if (!expandedKeys) {
          // 清空搜索时展开所有节点
          if (defaultExpandAll) {
            const getAllKeys = (nodes: TreeNodeData[]): React.Key[] => {
              const keys: React.Key[] = [];
              nodes.forEach((node) => {
                keys.push(node.key);
                if (node.children) {
                  keys.push(...getAllKeys(node.children));
                }
              });
              return keys;
            };
            setInternalExpandedKeys(getAllKeys(processedTreeData));
            setAutoExpandParent(true);
          } else {
            setInternalExpandedKeys(defaultExpandedKeys);
          }
        }
      }
    };

    // 处理复选框变化
    const handleCheck = useCallback(
      (checkedKeysValue: any, info: any) => {
        if (!checkable) {
          return;
        }

        // 确保 checkedKeys 是数组
        let newCheckedKeys: React.Key[] = [];

        if (Array.isArray(checkedKeysValue)) {
          newCheckedKeys = checkedKeysValue;
        } else if (checkedKeysValue && typeof checkedKeysValue === "object") {
          // 处理 { checked: [], halfChecked: [] } 格式
          const { checked = [] } = checkedKeysValue;
          newCheckedKeys = checked;
        } else {
          return;
        }

        // 设置用户交互标志
        setIsUserInteracting(true);

        // 更新内部状态（如果使用内部状态）
        if (externalCheckedKeys === undefined) {
          setInternalCheckedKeys(newCheckedKeys);
        }

        // 调用外部回调
        if (onCheck) {
          onCheck(newCheckedKeys, info);
        }

        // 延迟重置用户交互标志
        setTimeout(() => {
          setIsUserInteracting(false);
        }, 500);
      },
      [checkable, externalCheckedKeys, onCheck]
    );

    // 处理展开/折叠
    const handleExpand = (newExpandedKeys: React.Key[], info: any) => {
      if (onExpand) {
        // 如果有外部onExpand处理器，调用它
        onExpand(newExpandedKeys, info);
      } else {
        // 否则使用内部状态
        setInternalExpandedKeys(newExpandedKeys);
      }
      setAutoExpandParent(false);
    };

    // 渲染树节点标题，包括操作按钮
    const renderNodeTitle = useCallback(
      (node: TreeNodeData) => {
        // 使用节点自己的菜单项，如果没有则使用全局菜单项
        const nodeMenuItems = node.menuItems || menuItems || [];

        const menu = (
          <Menu
            onClick={({ key }: { key: string }) =>
              onMenuClick && onMenuClick(node, key as string)
            }
          >
            {nodeMenuItems.map((item: { key: string; label: string }) => (
              <Menu.Item key={item.key}>{item.label}</Menu.Item>
            ))}
          </Menu>
        );

        // 如果节点标题已经是 React 节点（来自 nodeTitleRender），直接使用
        const titleContent =
          typeof node.title === "string" ? (
            <Text ellipsis style={{ flex: 1, minWidth: 0 }}>
              {node.title}
            </Text>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }}>{node.title}</div>
          );

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: "8px",
            }}
          >
            {titleContent}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                justifyContent: "flex-end",
                gap: "8px",
                minWidth: "24px",
              }}
            >
              {/* 默认操作按钮 */}
              {showActions && onEdit && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onEdit(node);
                  }}
                  style={{
                    padding: "0 4px",
                    color: "#bfbfbf",
                    fontSize: "12px",
                  }}
                  title="编辑"
                />
              )}
              {showActions && onDelete && (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onDelete(node);
                  }}
                  style={{
                    padding: "0 4px",
                    color: "#bfbfbf",
                    fontSize: "12px",
                  }}
                  title="删除"
                />
              )}

              {/* 自定义菜单按钮 */}
              {customMenuButton && customMenuButton(node)}
              {!customMenuButton &&
                onMenuClick &&
                nodeMenuItems &&
                nodeMenuItems.length > 0 && (
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <Button
                      type="text"
                      icon={<EllipsisOutlined />}
                      size="small"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      style={{
                        padding: "0 4px",
                        color: "#bfbfbf",
                        fontSize: "12px",
                      }}
                      className="tree-node-add-button"
                      title="更多操作"
                    />
                  </Dropdown>
                )}
            </div>
          </div>
        );
      },
      [menuItems, onMenuClick, showActions, onEdit, onDelete, customMenuButton]
    );

    // 添加自定义标题到树数据
    const addCustomTitleToData = useCallback(
      (data: TreeNodeData[]): DataNode[] => {
        return data.map((node) => {
          const newNode: DataNode = {
            ...node,
            // 只有在非 customLayout 模式或需要显示操作按钮时才使用 renderNodeTitle
            // customLayout 模式下直接使用原始 title，避免阻止复选框点击
            title: customLayout ? node.title : renderNodeTitle(node),
          };

          if (node.children) {
            newNode.children = addCustomTitleToData(node.children);
          }

          return newNode;
        });
      },
      [customLayout, renderNodeTitle]
    );

    // 带自定义标题的树数据
    const treeDataWithCustomTitle = useMemo(() => {
      return addCustomTitleToData(filteredTreeData);
    }, [addCustomTitleToData, filteredTreeData]);

    // 构建默认右上角刷新按钮
    const defaultButtons = onRefresh
      ? [
          <Button
            key="refresh"
            type="link"
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            style={{
              fontSize: "12px",
              padding: "0 8px",
              color: "#bfbfbf",
            }}
          >
            刷新
          </Button>,
        ]
      : [];

    // 增强 switcherIconFunc 函数
    const switcherIconFunc = (props: any) => {
      const expanded = !!props.expanded;

      // 添加内联样式，确保不会被其他样式覆盖
      const iconStyle = {
        color: "#bfbfbf",
        transform: "none !important",
        transition: "none !important",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      };

      if (expanded) {
        // 向下箭头 - 使用内置图标
        return <CaretDownOutlined style={iconStyle} />;
      } else {
        // 向右箭头 - 使用内置图标
        return <CaretRightOutlined style={iconStyle} />;
      }
    };

    // 合并默认按钮和自定义按钮
    const allButtons = [...defaultButtons, ...buttons];

    // 如果使用自定义布局（搜索框固定，树可滚动）
    if (customLayout) {
      return (
        <>
          <style>{globalStyles}</style>
          <Card
            title={title}
            extra={
              extra ||
              (allButtons.length > 0 ? <Space>{allButtons}</Space> : null)
            }
            className={className}
            loading={loading}
            style={{
              height: typeof height === "number" ? `${height}px` : height,
              ...style,
            }}
            styles={{
              body: {
                padding: "12px 24px",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              },
              ...cardProps.styles,
              header: headerPadding
                ? { padding: headerPadding }
                : cardProps.styles?.header,
            }}
            {...cardProps}
          >
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              {/* 搜索框 - 固定位置 */}
              {showSearch && (
                <div
                  style={{
                    flexShrink: 0,
                    paddingBottom: 16,
                    backgroundColor: "white",
                    zIndex: 10,
                  }}
                >
                  <Input
                    placeholder={searchPlaceholder}
                    prefix={<SearchOutlined />}
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    allowClear
                  />
                </div>
              )}

              {/* 权限树 - 可滚动区域 */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  minHeight: 0,
                  paddingRight: "8px",
                  paddingBottom: "10px",
                  boxSizing: "border-box",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#d9d9d9 #f0f0f0",
                }}
              >
                {loading ? (
                  <div style={{ textAlign: "center", padding: 50 }}>
                    <Empty description="加载中..." />
                  </div>
                ) : (
                  <Tree
                    checkable={checkable}
                    checkedKeys={checkedKeys}
                    onCheck={handleCheck}
                    onSelect={handleSelect}
                    expandedKeys={currentExpandedKeys}
                    onExpand={handleExpand}
                    autoExpandParent={autoExpandParent}
                    treeData={treeDataWithCustomTitle as DataNode[]}
                    showLine={{ showLeafIcon: false }}
                    showIcon={showIcon}
                    checkStrictly={checkStrictly}
                    defaultCheckedKeys={defaultCheckedKeys}
                    style={{
                      background: "transparent",
                    }}
                    // customLayout 模式下不使用 titleRender，直接使用 treeData 中的 title
                    // 这样可以避免阻止复选框的点击事件
                    filterTreeNode={(node) => {
                      if (!searchValue) return true;
                      const titleText = getNodeTitleText(node as TreeNodeData);
                      return titleText
                        .toLowerCase()
                        .includes(searchValue.toLowerCase());
                    }}
                  />
                )}

                {filteredTreeData.length === 0 && !loading && (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Empty description="暂无数据" />
                  </div>
                )}
              </div>
            </div>
          </Card>
        </>
      );
    }

    // 默认布局（Card 包装）
    return (
      <>
        <style>{globalStyles}</style>

        <div
          ref={containerRef}
          style={{
            flex: 1,
            minHeight: 0,
            ...style,
          }}
          className={className}
        >
          <Card
            title={title}
            extra={
              extra || (
                <Space>
                  {allButtons.map((button, index) =>
                    React.cloneElement(button as React.ReactElement, {
                      key: index,
                    })
                  )}
                </Space>
              )
            }
            className="tree-panel"
            loading={loading}
            style={{
              height: typeof height === "number" ? `${height}px` : height,
            }}
            styles={{
              body: {
                padding: "0 16px",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                rowGap: 16,
              },
              ...cardProps.styles,
              // 确保 headerPadding 不被 cardProps.styles 覆盖
              header: headerPadding
                ? { padding: headerPadding }
                : cardProps.styles?.header,
            }}
            {...cardProps}
          >
            {showSearch && (
              <div className="tree-search-container" style={{}}>
                <Search
                  placeholder={searchPlaceholder}
                  allowClear
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSearch(e.target.value)
                  }
                />
              </div>
            )}

            {filteredTreeData.length > 0 ? (
              <div
                className="tree-container"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  paddingBottom: "32px",
                }}
              >
                <Tree
                  className="full-width-tree compact-tree"
                  showLine={false}
                  showIcon={showIcon}
                  onExpand={handleExpand}
                  expandedKeys={currentExpandedKeys}
                  autoExpandParent={autoExpandParent}
                  onSelect={handleSelect}
                  defaultSelectedKeys={defaultSelectedKeys}
                  selectedKeys={selectedKeys}
                  defaultExpandAll={defaultExpandAll}
                  treeData={treeDataWithCustomTitle as DataNode[]}
                  switcherIcon={switcherIconFunc}
                  // 复选框相关属性
                  checkable={checkable}
                  checkedKeys={checkedKeys}
                  onCheck={handleCheck}
                  defaultCheckedKeys={defaultCheckedKeys}
                  checkStrictly={checkStrictly}
                  style={{
                    width: "100%",
                    fontSize: "14px",
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Empty description="暂无数据" />
              </div>
            )}
          </Card>
        </div>
      </>
    );
  }
);

GenericPanelTree.displayName = "GenericPanelTree";

export default GenericPanelTree;
